/* eslint-disable no-undef-init */
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import Map, { Layer, MapRef, NavigationControl, Source } from 'react-map-gl';
import mapboxgl from 'utils/mapboxgl';
import { LngLatBounds } from 'mapbox-gl';
import type { FeatureCollection } from 'geojson';

import 'mapbox-gl/dist/mapbox-gl.css';
import { Button, DateSelect, Select, Typography } from 'design_system/src';
import {
	InterestPointType,
	InterestPointTypeValues,
} from 'models/repossession/enums/repossession.enums';
import Supercluster from 'supercluster';
import { momentLocal } from 'models/shared/helpers/dates';
import { ReactComponent as Close } from 'design_system/src/static/icons/close-lg.svg';
import { ReactComponent as Location } from 'design_system/src/static/icons/gps.svg';
import { ReactComponent as Moto } from 'design_system/src/static/icons/motorcycle.svg';
import { ReactComponent as Checkbox } from 'design_system/src/static/icons/checkmark.svg';
import { ReactComponent as Resolution } from 'design_system/src/static/icons/resolution.svg';
import { ReactComponent as Eye } from 'design_system/src/static/icons/eye.svg';

import profileDefaultOzoner from 'static/images/default_profile_ozoner.png';

import { Recovery } from 'models/repossession/interfaces/recovery.interface';
import {
	RecoveryFinalConditionsValues,
	RecoveryStatus,
} from 'models/repossession/enums/recovery.enums';
import useDateFilters from 'hooks/useDatesFilters';
import { fetchTilesLocationHistory } from 'models/geozon/controllers/tile.controller';
import { TileTrackerHistoryLocationDto } from 'models/geozon/interfaces/tile.interface';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { repossessionContext } from '../../RepossessionsContext';

import '../../recovery.scss';
import {
	activeInterestPointTypes,
	createClusterMarker,
	createPointMarker,
	CreateRouteSteps,
	hoursOptions,
	hotspotHeatLayerDefinition,
	hotspotTileLayerDefinition,
	PlainLocation,
} from '../../Repossessions.constants';

const tileLocationTypes = [
	InterestPointType.signalTileVehicle,
	InterestPointType.noSignalTileVehicle,
];

const getHistoryLabel = (loadingHistory: boolean, pointsCount: number) => {
	if (loadingHistory) return 'Cargando histórico...';
	if (pointsCount) return `${pointsCount} puntos históricos (90 días)`;
	return 'Sin puntos históricos';
};

export const SelectInterestPointsMap = () => {
	const mapRef = useRef<MapRef>(null);

	const {
		setCreateRouteStep,
		repossessionsData,
		interestPoints,
		gpsLocations,
		tileLocations,
		recoveriesData,
		selectedRepossessions,
		selectedInterestPoints,
		setSelectedInterestPoints,
	} = useContext(repossessionContext);

	const [currentLoan, setCurrentLoan] = useState<string>();
	const [openInterestPoint, setOpenInterestPoint] = useState<PlainLocation>();
	const [recoveries, setRecoveries] = useState<{
		[key: string]: Recovery[];
	}>({});
	const [locationsHistory, setLocationsHistory] = useState<
		TileTrackerHistoryLocationDto[]
	>([]);
	const [loadingHistory, setLoadingHistory] = useState(false);

	const { datesFilter, hasDatesFilter, setDatesFilter, setHasDatesFilter } =
		useDateFilters();

	const [interestPointTypeFilter, setInterestPointTypeFilter] = useState<
		Partial<Record<InterestPointType, boolean>>
	>({
		[InterestPointType.address]: true,
		[InterestPointType.geolocation]: true,
		[InterestPointType.signalTileVehicle]: true,
		[InterestPointType.noSignalTileVehicle]: true,
	});
	const [onlySelected, setOnlySelected] = useState(false);

	const [hoursFilter, setHoursFilter] = useState<number[]>([]);

	const [viewState, setViewState] = useState({
		longitude: -99.12766,
		latitude: 19.42847,
		zoom: 10,
	});
	const [displayedLocations, setDisplayedLocations] = useState<PlainLocation[]>(
		[],
	);
	useEffect(() => {
		setCurrentLoan(selectedRepossessions[0]?.loanId);
	}, [selectedRepossessions]);

	useEffect(() => {
		clearFilters();
		if (
			!currentLoan ||
			!Object.keys(recoveriesData).length ||
			!Object.keys(repossessionsData).length
		)
			return;
		setRecoveries(
			recoveriesData[currentLoan].reduce((acc: any, recovery) => {
				let idB: string = recovery.interestPoint.type;
				if (recovery.interestPoint.externalId)
					idB = recovery.interestPoint.externalId;
				else if (idB === InterestPointType.noSignalTileVehicle)
					idB = InterestPointType.signalTileVehicle;
				else if (idB === InterestPointType.noSignalVehicle)
					idB = InterestPointType.signalVehicle;

				const id = `${currentLoan}_${idB}`;
				const interestPointRecoveries = acc[id] ?? [];
				interestPointRecoveries.push(recovery);
				return {
					...acc,
					[id]: interestPointRecoveries,
				};
			}, {}) ?? {},
		);
	}, [currentLoan, repossessionsData, recoveriesData]);
	useEffect(() => {
		mapRef.current?.flyTo({
			center: [-99.12766, 19.42847],
			zoom: 10,
		});
		setOpenInterestPoint(undefined);
		setLocationsHistory([]);
	}, [currentLoan]);

	useEffect(() => {
		if (currentLoan === undefined) {
			setDisplayedLocations([]);
			return;
		}
		const locations = [...(interestPoints[currentLoan] ?? [])].filter(
			(point) => {
				if (!interestPointTypeFilter[point.locationType]) return false;

				if (hasDatesFilter) {
					const date = momentLocal(point.createdAt);
					const { initial, last } = datesFilter;
					if (date.isBefore(initial) || date.isAfter(last)) return false;
				}

				if (hoursFilter.length) {
					const date = momentLocal(point.createdAt);
					const hours = date.hours();
					if (!hoursFilter.includes(hours)) return false;
				}

				return true;
			},
		);
		const gps = gpsLocations[currentLoan];
		if (gps && interestPointTypeFilter[gps.locationType]) {
			locations.push(gps);
		}
		const tile = tileLocations[currentLoan];
		if (tile && interestPointTypeFilter[tile.locationType]) {
			locations.push(tile);
		}
		setDisplayedLocations(locations);
	}, [
		interestPointTypeFilter,
		interestPoints,
		gpsLocations,
		tileLocations,
		currentLoan,
		hoursFilter,
		datesFilter,
		hasDatesFilter,
	]);

	const supercluster = useMemo(
		() =>
			new Supercluster({
				radius: 75,
				maxZoom: 20,
			}),
		[],
	);

	const handleClusterClick = useCallback(
		(cluster) => {
			const clusterExpansionZoom = supercluster.getClusterExpansionZoom(
				cluster.id,
			);
			mapRef.current?.flyTo({
				center: cluster.geometry.coordinates,
				zoom: clusterExpansionZoom,
			});
		},
		[supercluster, viewState],
	);

	const onClickHandler = (point: PlainLocation) => {
		mapRef.current?.flyTo({
			center: {
				lat: point.coordinates.lat,
				lng: point.coordinates.long - 0.0075,
			},
			zoom: 15,
		});
		setOpenInterestPoint(point);

		const tileUuid = repossessionsData[point.loanId]?.tileUuid;
		if (tileLocationTypes.includes(point.locationType) && tileUuid) {
			setLoadingHistory(true);
			fetchTilesLocationHistory(tileUuid)
				.then((res) => {
					setLoadingHistory(false);
					setLocationsHistory(res.locations);
				})
				.catch((e) => {
					setLoadingHistory(false);
					commonErrorHandler(e);
				});
		} else {
			setLocationsHistory([]);
		}
	};

	const handleMapClick = (event: mapboxgl.MapLayerMouseEvent) => {
		const map = mapRef.current?.getMap();
		if (!map || !currentLoan) return;
		const features = map.queryRenderedFeatures(event.point, {
			layers: [hotspotTileLayerDefinition.id as string],
		});
		const raw = features[0]?.properties?.point;
		if (!raw) return;
		const histPoint: TileTrackerHistoryLocationDto =
			typeof raw === 'string' ? JSON.parse(raw) : raw;
		const repo = repossessionsData[currentLoan];
		const waypoint: PlainLocation = {
			loanId: currentLoan,
			repossessionId: repo?.repossessionId,
			vehicleId: repo?.internalId,
			locationType: InterestPointType.signalTileVehicle,
			coordinates: { lat: histPoint.latitude, long: histPoint.longitude },
			createdAt: histPoint.timestamp,
			id: `${currentLoan}_hist_${histPoint.timestamp}`,
		};
		mapRef.current?.flyTo({
			center: {
				lat: waypoint.coordinates.lat,
				lng: waypoint.coordinates.long - 0.0075,
			},
			zoom: 15,
		});
		setOpenInterestPoint(waypoint);
	};

	const hotspotsLayer = useMemo(() => {
		const source: FeatureCollection = {
			type: 'FeatureCollection',
			features: locationsHistory.map((point, i) => ({
				id: i,
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [point.longitude, point.latitude],
				},
				properties: {
					weight: 6 / locationsHistory.length,
					point,
				},
			})),
		};
		return source?.features?.length ? (
			<Source type="geojson" data={source}>
				<Layer {...hotspotHeatLayerDefinition} />
				<Layer {...hotspotTileLayerDefinition} />
			</Source>
		) : null;
	}, [locationsHistory]);

	const markers = useMemo(() => {
		if (!currentLoan) return [];
		supercluster.load(
			displayedLocations
				.filter(
					(point) =>
						!onlySelected ||
						selectedInterestPoints[currentLoan]?.some(
							(item) => item.id === point.id,
						),
				)
				.map((point) => ({
					type: 'Feature',
					geometry: {
						type: 'Point',
						coordinates: [point.coordinates.long, point.coordinates.lat],
					},
					properties: point,
				})),
		);

		const mainPoints = supercluster
			.getClusters([-180, -85, 180, 85], Math.round(viewState.zoom))
			.map((cluster) => {
				if (cluster.properties.cluster) {
					return createClusterMarker(cluster, handleClusterClick);
				}
				const point = cluster.properties as PlainLocation;
				const selected = !!selectedInterestPoints[currentLoan]?.find(
					(item) => item.id === point.id,
				);
				const open = openInterestPoint?.id === point.id;
				const visitCount =
					recoveries[point.id]?.filter((recovery) => recovery.comment)
						?.length ?? 0;
				return createPointMarker(
					cluster,
					point,
					selected,
					open,
					visitCount,
					onClickHandler,
				);
			});

		return [...mainPoints];
	}, [
		displayedLocations,
		recoveries,
		selectedInterestPoints,
		supercluster,
		viewState.zoom,
		openInterestPoint,
		onlySelected,
	]);

	const closeInterestPoint = () => {
		setOpenInterestPoint(undefined);
	};

	const fitView = () => {
		const puntos = [...displayedLocations];

		if (!puntos.length) return;

		// Usamos LngLatBounds importado de 'mapbox-gl'
		const bounds = new LngLatBounds();

		puntos.forEach((punto) => {
			bounds.extend([punto.coordinates.long, punto.coordinates.lat]);
		});

		mapRef.current?.fitBounds(bounds, {
			padding: 80,
			maxZoom: 16,
			duration: 1000,
		});
	};

	const onContinueClick = () => {
		const currentIndex = selectedRepossessions.findIndex(
			(item) => item.loanId === currentLoan,
		);
		if (currentIndex + 1 === selectedRepossessions.length) {
			setCreateRouteStep(CreateRouteSteps.createRoute);
		} else {
			setCurrentLoan(selectedRepossessions[currentIndex + 1].loanId);
		}
	};

	const onBackClick = () => {
		const currentIndex = selectedRepossessions.findIndex(
			(item) => item.loanId === currentLoan,
		);
		if (currentIndex === 0) {
			setCreateRouteStep(CreateRouteSteps.filterRepossessions);
		} else {
			setCurrentLoan(selectedRepossessions[currentIndex - 1].loanId);
		}
	};

	const clearFilters = () => {
		setHasDatesFilter(false);
		setHoursFilter([]);
		setInterestPointTypeFilter({
			[InterestPointType.address]: true,
			[InterestPointType.geolocation]: true,
			[InterestPointType.signalTileVehicle]: true,
			[InterestPointType.noSignalTileVehicle]: true,
		});
	};

	return currentLoan ? (
		<div className="dim_100_per flex_content_col inner_container bg_neutral_300">
			<div className="repossession_map_input_cont z_index_20 display_flex flex_col flex_gap_md flex_align_start">
				<div className="display_flex flex_gap_md">
					<DateSelect
						value={datesFilter}
						hasValue={hasDatesFilter}
						setHasValue={setHasDatesFilter}
						placeholder="Seleccionar fecha"
						setValue={(e: any) => {
							setDatesFilter(e);
							setHasDatesFilter(true);
						}}
					/>
					<Select
						placeholder="Seleccionar hora"
						variant="checkbox"
						options={hoursOptions}
						value={hoursFilter}
						onChange={(e: any) => setHoursFilter(e.target.value)}
						hideAfterOptions={2}
					/>
				</div>
				<div className="shadow_hard br_md p_md flex_center bg_neutral_0 flex_gap_sm">
					<Moto className="text_primary_300 dim_xl" />
					<Typography scale="medium" weight="600">
						Moto{' '}
						{selectedRepossessions.findIndex(
							(item) => item.loanId === currentLoan,
						) + 1}{' '}
						de {selectedRepossessions.length}:{' '}
						{repossessionsData[currentLoan]?.internalId}
					</Typography>
				</div>
				<div className="bg_neutral_0 br_md p_md">
					{Object.entries(InterestPointTypeValues)
						.filter(([key]) =>
							activeInterestPointTypes.includes(key as InterestPointType),
						)
						.map(([key, type]) => (
							<div
								key={key}
								className="display_flex flex_align_center flex_gap_xs"
							>
								<type.icon className={` text_${type.realColor} dim_xl`} />
								<Typography scale="small" weight="600" className="flex_grow_1">
									{type.title}
								</Typography>
								<div
									className={`repossession_map_input_checkbox br_xs m_l_md ${
										interestPointTypeFilter[key as InterestPointType]
											? 'selected'
											: ''
									}`}
									onClick={() =>
										setInterestPointTypeFilter({
											...interestPointTypeFilter,
											[key]: !interestPointTypeFilter[key as InterestPointType],
										})
									}
									onKeyDown={() =>
										setInterestPointTypeFilter({
											...interestPointTypeFilter,
											[key]: !interestPointTypeFilter[key as InterestPointType],
										})
									}
									role="checkbox"
									aria-checked={
										interestPointTypeFilter[key as InterestPointType]
									}
									tabIndex={0}
								>
									<Checkbox className="repossession_map_input_checkbox_icon center" />
								</div>
							</div>
						))}
					<div className="display_flex flex_align_center flex_gap_xs flex_justify_between">
						<div className="display_flex flex_align_center flex_gap_xs">
							<Eye className="text_primary_300 dim_xl" />
							<Typography scale="small" weight="600">
								Ver solo seleccionadas
							</Typography>
						</div>
						<div
							className={`repossession_map_input_checkbox br_xs m_l_md ${
								onlySelected ? 'selected' : ''
							}`}
							onClick={() => setOnlySelected(!onlySelected)}
							onKeyDown={() => setOnlySelected(!onlySelected)}
							role="checkbox"
							aria-checked={onlySelected}
							tabIndex={0}
						>
							<Checkbox className="repossession_map_input_checkbox_icon center" />
						</div>
					</div>
				</div>
			</div>

			<div className="repossession_map_counter flex_center">
				<div className="repossession_map_counter_icon p_md bg_primary_300 br_circle display_flex">
					<Location className="dim_xl text_neutral_0" />
				</div>
				<div className="p_y_sm p_l_xl bg_neutral_0 br_md">
					<Typography scale="small" weight="400" className="p_r_lg p_l_xs">
						Puntos seleccionados:{' '}
						{selectedInterestPoints[currentLoan]?.length ?? 0}
					</Typography>
				</div>
			</div>
			{openInterestPoint && (
				<div
					className={`repossession_map_interest_point_info bg_neutral_0 br_md center ${
						recoveries[openInterestPoint.id]?.length ? 'h_80_per' : ''
					}`}
				>
					<div className="flex_content_col">
						<div className="flex_header flex_center flex_justify_between p_x_lg p_y_sm border_1 border_b_solid border_neutral_300">
							<Typography scale="large" weight="600">
								{InterestPointTypeValues[openInterestPoint.locationType]
									?.title ?? openInterestPoint.locationType}
							</Typography>
							{tileLocationTypes.includes(openInterestPoint.locationType) && (
								<Typography scale="small" weight="400" className="m_r_md">
									{getHistoryLabel(loadingHistory, locationsHistory.length)}
								</Typography>
							)}
							<Close className="dim_lg" onClick={closeInterestPoint} />
						</div>
						<div className="flex_body p_lg">
							{recoveries[openInterestPoint.id]?.filter(
								(recovery) => recovery.comment,
							)?.length ? (
								<div className="display_flex flex_gap_sm flex_col">
									{recoveries[openInterestPoint.id]
										?.filter((recovery) => recovery.comment)
										?.map((recovery) => (
											<div
												key={recovery._id}
												className="br_md border_1 border_solid border_neutral_300 p_md display_flex flex_gap_sm"
											>
												<div className="br_circle bg_neutral_600 dim_xxl overflow_hidden">
													<img
														src={
															recovery.route.team?.[0]?.image?.url ||
															profileDefaultOzoner
														}
														alt=""
														className="dim_100_per img_cover"
													/>
												</div>
												<div className="flex_1">
													<div className="flex_center flex_justify_between m_b_md w_100_per">
														<Typography scale="large" weight="600" className="">
															{recovery.route.team?.[0]?.name}
														</Typography>
														<Typography scale="small" weight="600" className="">
															{momentLocal(recovery.updatedAt).format(
																'DD/MM/YYYY',
															)}
														</Typography>
													</div>
													{recovery.finalCondition && (
														<Typography
															scale="medium"
															weight="600"
															className=" text_center"
															textColor="neutral_900"
														>
															{
																RecoveryFinalConditionsValues[
																	recovery.finalCondition
																].title
															}
														</Typography>
													)}
													<Typography scale="medium" weight="400" className="">
														{recovery.comment}
													</Typography>
												</div>
											</div>
										))}
								</div>
							) : (
								<div className="flex_center dim_100_per">
									<Typography
										scale="medium"
										weight="400"
										className="text_center"
									>
										Aún no hay notas disponibles.
									</Typography>
								</div>
							)}
						</div>
						<div className="flex_footer display_flex flex_justify_end p_x_lg p_y_sm m_t_none border_1 border_t_solid border_neutral_300">
							{!selectedInterestPoints[currentLoan] ||
							!selectedInterestPoints[currentLoan].find(
								(item) => item.id === openInterestPoint.id,
							) ? (
								<Button
									variant="ghost"
									scale="small"
									onClick={() => {
										setSelectedInterestPoints({
											...selectedInterestPoints,
											[currentLoan]: [
												...(selectedInterestPoints[currentLoan] ?? []),
												openInterestPoint,
											],
										});
										closeInterestPoint();
									}}
								>
									Agregar a la ruta
								</Button>
							) : (
								<Button
									variant="ghost"
									color="red"
									scale="small"
									onClick={() => {
										setSelectedInterestPoints({
											...selectedInterestPoints,
											[currentLoan]: selectedInterestPoints[
												currentLoan
											]?.filter((item) => item.id !== openInterestPoint.id),
										});
										closeInterestPoint();
									}}
								>
									Eliminar de la ruta
								</Button>
							)}
						</div>
					</div>
				</div>
			)}
			<Button
				className="repossession_map_next_button"
				onClick={onContinueClick}
				color={
					!selectedInterestPoints[currentLoan]?.length ? 'secondary' : 'primary'
				}
			>
				{!selectedInterestPoints[currentLoan]?.length
					? 'Omitir Credito'
					: 'Continuar'}
			</Button>
			<Button
				className="repossession_map_prev_button"
				color="grey"
				onClick={onBackClick}
			>
				Anterior
			</Button>
			<Button
				className="repossession_map_fit_button"
				variant="icon"
				subvariant="edit"
				color="grey"
				onClick={fitView}
				icon={<Resolution />}
			/>
			<Map
				mapLib={mapboxgl}
				mapboxAccessToken={process.env.REACT_APP_MAPBOX_API_KEY || ''}
				onMove={(evt) => setViewState(evt.viewState)}
				onClick={handleMapClick}
				mapStyle="mapbox://styles/mapbox/streets-v9"
				reuseMaps
				ref={mapRef}
				{...viewState}
			>
				<NavigationControl
					position="bottom-right"
					style={{ marginBottom: '11rem' }}
				/>
				{hotspotsLayer}
				{markers}
			</Map>
		</div>
	) : null;
};
